import i18n from 'i18next';
import {AiQueryCapabilityResult} from 'views/admin/adminAiQueryApi';
import {getProjectionForCapability, TableProjection} from 'views/admin/aiQueryColumns';
import {cellValueToText} from 'views/admin/aiQueryCsv';

/**
 * Inline dataset tables (see `helpers/ai_query/inline_tables.py`). The agent
 * writes `[[table:<dataset_handle>]]` on its own line where a table belongs,
 * and the stream sends that dataset's rows as soon as its tool finishes, so the
 * table is drawn in place from structured rows instead of model-written Markdown.
 */
const MARKER_LINE = /^[ \t]*\[\[table:([A-Za-z0-9_]+)\]\][ \t]*$/;
const MARKER_LINES = /^[ \t]*\[\[table:([A-Za-z0-9_]+)\]\][ \t]*$/gm;
const MARKER_PREFIX = '[[table:';

/** Rows an answer's table shows before "Show more"; the download holds them all. */
export const INLINE_TABLE_VISIBLE_ROWS = 10;

/**
 * Datasets that are sources to cite, not rows to show (Dash's one row is its own
 * answer; doc search rows are excerpts; a read document's row is its text). A
 * marker naming one draws nothing, matching the server's
 * `NON_TABULAR_CAPABILITIES`.
 */
const NON_TABULAR_CAPABILITIES = new Set(['ask_dash', 'search_docs', 'read_document']);

/** Whether a marker naming `result` should draw a table. */
export const isTabularDataset = (result: AiQueryCapabilityResult): boolean =>
  !NON_TABULAR_CAPABILITIES.has(result.capability);

export type AnswerSegment = {kind: 'markdown'; markdown: string} | {kind: 'table'; handle: string};

/** Datasets by handle: streamed previews, replaced by the done frame's full results. */
export const datasetsByHandle = (
  streamed: Record<string, AiQueryCapabilityResult>,
  results: AiQueryCapabilityResult[]
): Record<string, AiQueryCapabilityResult> => {
  const merged = {...streamed};
  for (const result of results) {
    if (result.handle) {
      merged[result.handle] = result;
    }
  }
  return merged;
};

/** Whether a (trimmed) last line could still grow into a marker. */
const isPartialMarker = (line: string): boolean =>
  line.length > 0 &&
  (MARKER_PREFIX.startsWith(line) || (line.startsWith(MARKER_PREFIX) && !MARKER_LINE.test(line)));

/**
 * Split an answer into Markdown runs and table markers, in order. While
 * streaming, a trailing line that is still becoming a marker is held back so
 * it never flashes as text.
 */
export const splitAnswerSegments = (answer: string, isStreaming: boolean): AnswerSegment[] => {
  const lines = answer.split('\n');
  if (isStreaming && isPartialMarker(lines[lines.length - 1].trim())) {
    lines.pop();
  }
  const segments: AnswerSegment[] = [];
  let run: string[] = [];
  const flush = () => {
    const markdown = run.join('\n');
    if (markdown.trim()) {
      segments.push({kind: 'markdown', markdown});
    }
    run = [];
  };
  for (const line of lines) {
    const match = line.match(MARKER_LINE);
    if (match) {
      flush();
      segments.push({kind: 'table', handle: match[1]});
    } else {
      run.push(line);
    }
  }
  flush();
  return segments;
};

/** Handles of the datasets an answer places as tables, in order. */
export const inlineTableHandles = (answer: string): string[] =>
  Array.from(answer.matchAll(MARKER_LINES), (match) => match[1]);

/**
 * The dataset's usual projection without columns that are blank on every row
 * (e.g. no team has a primary contact), which only add clutter to an answer.
 */
export const inlineTableProjection = (result: AiQueryCapabilityResult): TableProjection => {
  const projection = getProjectionForCapability(result.capability, result.items);
  return {
    ...projection,
    columns: projection.columns.filter((col) =>
      result.items.some((row) => cellValueToText(row, col) !== '')
    ),
  };
};

const escapeCell = (text: string): string => text.replace(/\|/g, '\\|').replace(/\n/g, ' ');

const markdownTable = (result: AiQueryCapabilityResult): string => {
  const projection = inlineTableProjection(result);
  if (projection.columns.length === 0) {
    return '';
  }
  const header = `| ${projection.columns.map((col) => escapeCell(col.header)).join(' | ')} |`;
  const divider = `|${projection.columns.map(() => '---').join('|')}|`;
  const rows = result.items.map(
    (row) =>
      `| ${projection.columns.map((col) => escapeCell(cellValueToText(row, col))).join(' | ')} |`
  );
  const total = result.row_count ?? result.items.length;
  const table = [header, divider, ...rows].join('\n');
  return total > rows.length
    ? `${table}\n\n${i18n.t('ai_query_inline_table_copied_count', {
        shown: rows.length.toLocaleString(),
        total: total.toLocaleString(),
      })}`
    : table;
};

/**
 * The answer with each marker replaced by a plain Markdown table of every row
 * the stream carried, plus how many there are in all when that's a preview
 * (for copying, and for the chart offers that read Markdown tables). Markers
 * with no rows, or naming a non-tabular dataset, are dropped, matching what
 * the drawer shows.
 */
export const expandInlineTables = (
  answer: string,
  datasets: Record<string, AiQueryCapabilityResult>
): string =>
  answer.replace(MARKER_LINES, (_line, handle: string) => {
    const result = datasets[handle];
    return result && result.items.length > 0 && isTabularDataset(result)
      ? markdownTable(result)
      : '';
  });
